'use client';

import {
  type Factiii,
  FactiiiRequirement,
  FactiiiStatus
} from '@shared/all/domains/factiiis';
import type { Result } from '@shared/all/domains/request';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useSpace from '@shared/utils/hooks/useSpace';
import useUser from '@shared/utils/hooks/useUser';
import {
  type InfiniteData,
  useInfiniteQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  Archive,
  Bookmark,
  EllipsisVertical,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';

import { client, type RouterOutputs } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Confirmation from '../common/Confirmation';
import AddFactiiiModal from '../spaces/AddFactiiiModal';
import Input from '../UI/Input';
import FactiiiEdit from './FactiiiEdit';

type Props = {
  slug: string;
  space: RouterOutputs['spaces']['get'];
  // Board mode: only the factiiis carrying the LIST requirement, i.e. the
  // columns the board actually renders. Every other factiii is noise there.
  onlyLists?: boolean;
};

// Retire, delete and edit all target one factiii at a time, so they share a
// single state slot instead of three booleans that can disagree.
type TargetedFactiii = {
  mode: 'retire' | 'delete' | 'edit';
  factiii: Factiii;
} | null;

const FactiiisList = (props: Props) => {
  const { pinSpaceFactiii, isOwner: isSpaceOwner } = useSpace();
  const { currentUser } = useUser();
  const { getFactiiiIds } = useFactiiis(client);

  const [buttonLoading, setButtonLoading] = useState(false);
  const [addFactiiiModalOpen, setAddFactiiiModalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [targeted, setTargeted] = useState<TargetedFactiii>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const queryClient = useQueryClient();
  const router = useRouter();

  const listMode = props.onlyLists === true;
  const noun = listMode ? 'list' : 'factiii';
  const selectedFactiii = targeted?.mode === 'retire' ? targeted.factiii : null;
  const deletingFactiii = targeted?.mode === 'delete' ? targeted.factiii : null;
  const editingFactiii = targeted?.mode === 'edit' ? targeted.factiii : null;

  // `getFactiiis` returns every list in one page, so list mode filters the
  // fetched rows instead of re-querying search (search cannot filter by
  // requirement, so it would leak non-list factiiis back in).
  const queryKey = listMode
    ? ['FactiiiList', props.slug, 'lists']
    : ['FactiiiList', props.slug, query];
  const {
    data,
    isLoading: loading,
    error,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }): Promise<{ factiiis: Factiii[] }> => {
      if (query && !listMode) {
        const result = await unwrapFromResult(
          client.search.factiiis.query({
            query,
            cursor: pageParam,
            spaceSlug: props.slug
          })
        );
        const factiiis = await getFactiiiIds(result.factiiis.map((f) => f.id));
        return { factiiis };
      } else {
        const { factiiiIds } = await unwrapFromResult(
          client.factiiis.getFactiiis.query({
            slug: props.slug,
            cursor: pageParam,
            type: 'SPACE',
            ...(listMode && { onlyLists: true })
          })
        );
        const factiiis = await getFactiiiIds(factiiiIds);
        return { factiiis };
      }
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => {
      if (listMode) return undefined;
      const factiiis = lastPage.factiiis;
      if (factiiis.length < 10) return undefined;
      return factiiis[factiiis.length - 1]?.id;
    }
  });

  const fetchedFactiiis = data?.pages.flatMap((page) => page.factiiis) ?? [];
  const visibleFactiiis =
    listMode && query
      ? fetchedFactiiis.filter((factiii) =>
          `${factiii.name} ${factiii.description ?? ''}`
            .toLowerCase()
            .includes(query.toLowerCase())
        )
      : fetchedFactiiis;

  // The board reads its columns from its own cache, so anything that adds,
  // renames, retires or removes a list has to drop that cache too.
  const invalidateBoard = () => {
    if (!listMode) return;
    void queryClient.invalidateQueries({
      queryKey: ['boardFactiiis', props.slug]
    });
  };

  // The owner adds, edits, retires and pins; factiiis.add accepts nobody else.
  const isOwner = props.space ? isSpaceOwner(props.space) : false;

  const retireFactiiiHandler = async () => {
    if (!props.slug || !selectedFactiii) return;
    const isRetired = selectedFactiii.status === FactiiiStatus.RETIRED;
    setButtonLoading(true);
    // Only result[0] (the error slot) is read, so the payload type is free.
    let result: Result<unknown>;

    if (isRetired)
      result = await client.factiiis.reactivate.mutate({
        factiiiId: selectedFactiii.id,
        spaceSlug: props.slug
      });
    else
      result = await client.factiiis.retire.mutate({
        factiiiId: selectedFactiii.id,
        spaceSlug: props.slug
      });
    if (!result[0]) {
      queryClient.setQueryData<InfiniteData<{ factiiis: Factiii[] }>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;

          const newPages = oldData.pages.map((page) => ({
            ...page,
            factiiis: page.factiiis.map((f) =>
              f.id === selectedFactiii.id
                ? {
                    ...f,
                    status: isRetired
                      ? FactiiiStatus.APPROVED
                      : FactiiiStatus.RETIRED
                  }
                : f
            )
          }));

          return {
            ...oldData,
            pages: newPages
          };
        }
      );
      invalidateBoard();
    }
    setButtonLoading(false);
    setTargeted(null);
  };

  const deleteFactiiiHandler = async () => {
    if (!props.slug || !deletingFactiii) return;
    setButtonLoading(true);
    const [error] = await client.factiiis.delete.mutate({
      factiiiId: deletingFactiii.id,
      spaceSlug: props.slug
    });
    if (!error) {
      queryClient.setQueryData<InfiniteData<{ factiiis: Factiii[] }>>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              factiiis: page.factiiis.filter((f) => f.id !== deletingFactiii.id)
            }))
          };
        }
      );
      invalidateBoard();
    }
    setButtonLoading(false);
    setTargeted(null);
  };

  const toggleMenu = (id: string) => {
    setActiveMenu(activeMenu === id ? null : id);
  };

  return (
    <>
      <div className="flex min-h-[700px] flex-col p-2">
        <div className="space-y-2">
          <div className="relative w-full">
            <div className="relative w-full">
              <Input
                value={query}
                className={'mb-2 w-full rounded'}
                label={
                  loading
                    ? 'Loading...'
                    : listMode
                      ? 'Search lists...'
                      : 'Search Factiiis...'
                }
                onChange={(e) => setQuery(e)}
              />
              {loading && (
                <Loader2 className="absolute inset-y-0 right-6 top-6 animate-spin text-textSecondary/60" />
              )}
            </div>
            {currentUser.isAuthenticated && isOwner && (
              // One compact row, not a label on the left with a round button
              // stranded on the right: the whole row is the target and it costs
              // half the vertical space, which the list above it needs.
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  setAddFactiiiModalOpen(true);
                }}
                className="hover:text-text mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-textSecondary/80 transition-colors hover:bg-background-light"
              >
                <Plus size={16} />
                Make a new {noun}
              </button>
            )}
          </div>

          {/* Factiiis List with Hamburger Menu */}
          {visibleFactiiis.length === 0 && !loading ? (
            <p className="p-4 text-base font-thin">No {noun}s found.</p>
          ) : (
            visibleFactiiis.map((factiii) => {
              const isPinned = props.space?.pinnedFactiiis?.some(
                (f) => f.id === factiii.id
              );
              const isFactiiiOwner =
                isOwner && (factiii.spaceSlug || props.slug) === props.slug;
              const isRetired = factiii.status === FactiiiStatus.RETIRED;
              const canPin = isFactiiiOwner && !isRetired;
              const menuId = `menu-${factiii.id}`;
              const showMenu = activeMenu === menuId;

              return (
                <div
                  className={`mb-1 flex items-center justify-between rounded-md p-4 hover:bg-background-light lg:px-2 ${
                    isRetired
                      ? 'bg-background-secondary/50 opacity-70'
                      : 'bg-background-secondary'
                  }`}
                  key={factiii.id}
                >
                  <Link
                    className="mr-3 flex min-w-0 flex-1"
                    href={`/fids/${factiii.id}`}
                  >
                    <Avatar
                      factiiiAvatarKey={factiii.avatarKey ?? null}
                      factiiiId={factiii.id}
                      size={50}
                      isRetired={isRetired}
                    />
                    <div className="flex min-w-0 flex-col">
                      <div className="ml-2 truncate">
                        {factiii.name || 'Unnamed Factiii'}
                        {isRetired && (
                          <span className="ml-2 text-xs text-textGray">
                            (Retired)
                          </span>
                        )}
                      </div>
                      {/* The description is what tells two similarly named
                          lists apart, so it reads before the id/slug line. */}
                      <div className="ml-2 line-clamp-2 text-sm text-textSecondary">
                        {factiii.description || 'No description yet'}
                      </div>
                      {!listMode && (
                        <div className="ml-2 truncate">{`factiiis/${factiii.id}`}</div>
                      )}
                    </div>
                  </Link>

                  {/* Hamburger Menu */}
                  {(isFactiiiOwner || canPin) && (
                    <div
                      id={menuId}
                      className="relative flex shrink-0 items-baseline"
                    >
                      <button
                        onClick={() => toggleMenu(menuId)}
                        className="rounded-full p-1.5 text-textSecondary hover:bg-background-light hover:text-textPrimary"
                        aria-label="More options"
                      >
                        <EllipsisVertical className="size-5" />
                      </button>

                      {/* Dropdown Menu */}
                      {showMenu && (
                        <div
                          ref={(el) => {
                            menuRefs.current[menuId] = el;
                          }}
                          className="absolute right-0 top-full z-10 mt-1 w-48 origin-top-right rounded-md bg-background-secondary shadow-lg ring-1 ring-ds-border-standard focus:outline-none"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="menu-button"
                        >
                          <div className="py-1" role="none">
                            {/* Edit name / description in place */}
                            {isFactiiiOwner && (
                              <button
                                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-background-light"
                                role="menuitem"
                                onClick={() => {
                                  setTargeted({ mode: 'edit', factiii });
                                  setActiveMenu(null);
                                }}
                              >
                                <Pencil className="size-4" />
                                <span>Edit {noun}</span>
                              </button>
                            )}

                            {/* Pin/Unpin to space */}
                            {canPin && (
                              <button
                                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-background-light"
                                role="menuitem"
                                onClick={() => {
                                  pinSpaceFactiii({
                                    id: factiii.id,
                                    name: factiii.name,
                                    slug: factiii.slug,
                                    spaceSlug: factiii.spaceSlug ?? props.slug,
                                    avatarKey: factiii.avatarKey,
                                    requirements: factiii.requirements
                                  });
                                  setActiveMenu(null);
                                }}
                              >
                                <Bookmark className="size-4" />
                                <span>
                                  {isPinned
                                    ? 'Unpin from space'
                                    : 'Pin to space'}
                                </span>
                              </button>
                            )}

                            {/* Retire/Reactivate Option */}
                            {[
                              FactiiiStatus.APPROVED,
                              FactiiiStatus.RETIRED
                            ].includes(factiii.status as FactiiiStatus) &&
                              isFactiiiOwner && (
                                <button
                                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-background-light"
                                  role="menuitem"
                                  onClick={() => {
                                    setTargeted({ mode: 'retire', factiii });
                                    setActiveMenu(null);
                                  }}
                                >
                                  {isRetired ? (
                                    <>
                                      <RefreshCw className="size-4" />
                                      <span>Reactivate {noun}</span>
                                    </>
                                  ) : (
                                    <>
                                      <Archive className="size-4" />
                                      <span>Retire {noun}</span>
                                    </>
                                  )}
                                </button>
                              )}

                            {/* Delete (soft) — owner only */}
                            {isFactiiiOwner && (
                              <button
                                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-destructive hover:bg-background-light"
                                role="menuitem"
                                onClick={() => {
                                  setTargeted({ mode: 'delete', factiii });
                                  setActiveMenu(null);
                                }}
                              >
                                <Trash2 className="size-4" />
                                <span>Delete {noun}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {error && (
            <p className="p-4 text-base font-thin">
              An error occurred. Please try again later.
            </p>
          )}
        </div>
        {hasNextPage && (
          <div className="flex w-full justify-center">
            <InView onChange={(isVisible) => isVisible && fetchNextPage()}>
              <Loader2 className="animate-spin" height={50} width={50} />
            </InView>
          </div>
        )}
      </div>
      {selectedFactiii && (
        <Confirmation
          title="Are you sure?"
          message={`${selectedFactiii.name || 'This'} ${noun} will be ${
            selectedFactiii.status === FactiiiStatus.RETIRED
              ? 'added back'
              : 'retired. You can add it back later'
          }.`}
          cancelHandler={() => setTargeted(null)}
          actionButtonText={
            selectedFactiii.status === FactiiiStatus.RETIRED
              ? 'Reactivate'
              : 'Retire'
          }
          confirmHandler={() => {
            void retireFactiiiHandler();
          }}
          actionButtonLoading={buttonLoading}
        />
      )}
      {deletingFactiii && (
        <Confirmation
          title={`Delete ${noun}?`}
          message={`"${deletingFactiii.name || 'This'}" ${noun} will be deleted and removed from this space. This can't be undone here.`}
          cancelHandler={() => setTargeted(null)}
          actionButtonText="Delete"
          confirmHandler={() => {
            void deleteFactiiiHandler();
          }}
          actionButtonLoading={buttonLoading}
        />
      )}
      {editingFactiii && (
        <FactiiiEdit
          factiii={editingFactiii}
          factiiiId={editingFactiii.id}
          spaceSlug={props.slug}
          closeHandler={() => {
            setTargeted(null);
            // FactiiiEdit writes through the shared factiii store, so a refetch
            // rebuilds these rows with the new name and description.
            void refetch();
            invalidateBoard();
          }}
        />
      )}
      {addFactiiiModalOpen && (
        <AddFactiiiModal
          closeHandler={(factiii) => {
            setAddFactiiiModalOpen(false);
            invalidateBoard();
            // A new list belongs on the board the user is already looking at,
            // so only the general factiii flow navigates to the factiii page.
            if (factiii && !listMode) router.push(`/fids/${factiii.id}`);
          }}
          type={'SPACE'}
          slug={props.slug}
          refetch={refetch}
          {...(listMode && { forcedRequirements: [FactiiiRequirement.LIST] })}
        />
      )}
    </>
  );
};

export default FactiiisList;
